/** @jsxImportSource @emotion/react */
'use memo'

import { ExportOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import usePaymentProviderConnection, {
  AccountDetails,
  PaymentProviderId,
  PaymentProviderStatus,
  ProviderError,
} from '@sportninja/common/hooks/usePaymentProviderConnection'
import { Alert, Badge, Spin } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import Icon from 'src/components/Icon'
import Text from 'src/components/ts-components/Text'
import { UserData } from '..'
import { PAYMENT_PROVIDERS } from 'src/hooks/usePaymentProvider'
import { getErrorMessage as getErrorMessageFromError } from '@sportninja/common/utils/utils'

type Account = any

// Country IDs for SportsPay support
const COUNTRY_ID_CANADA = 36
const COUNTRY_ID_USA = 226
const SUPPORTED_SPORTSPAY_COUNTRY_IDS = [COUNTRY_ID_CANADA, COUNTRY_ID_USA]

type PaymentProviderProps = {
  id: string
  name: string
  description: string
  logo: React.ReactNode
  isRecommended?: boolean
  hasCompetitiveRates?: boolean
  connectUrl?: string
  contactUrl?: string
  isConnected?: boolean
  accountDetails?: AccountDetails | null
  accountStatus?: PaymentProviderStatus | null
  shouldDisplayConnectButton?: boolean
}

// --- Shared styles (static, outside components) ---

const statusBannerCss = {
  error: css`
    padding: 8px;
    margin-top: 8px;
    background-color: rgba(255, 77, 79, 0.1);
    border: 1px solid ${colors.ERROR_100};
    .ant-alert-message {
      color: ${colors.WHITE};
      font-weight: 600;
    }
    .ant-alert-description {
      color: ${colors.WHITE};
    }
    .ant-alert-icon {
      color: ${colors.ERROR_100};
    }
  `,
  info: css`
    padding: 8px;
    margin-top: 8px;
    background-color: rgba(90, 166, 255, 0.1);
    border: 1px solid ${colors.INFO_200};
    .ant-alert-message {
      color: ${colors.WHITE};
      font-weight: 600;
    }
    .ant-alert-description {
      color: ${colors.WHITE};
    }
    .ant-alert-icon {
      color: ${colors.INFO_200};
    }
  `,
  warning: css`
    padding: 8px;
    margin-top: 8px;
    background-color: rgba(255, 204, 49, 0.1);
    border: 1px solid ${colors.WARNING_200};
    .ant-alert-message {
      color: ${colors.WHITE};
      font-weight: 600;
    }
    .ant-alert-description {
      color: ${colors.WHITE};
    }
    .ant-alert-icon {
      color: ${colors.WARNING_200};
    }
  `,
  success: css`
    padding: 8px;
    margin-top: 8px;
    background-color: rgba(102, 255, 153, 0.1);
    border: 1px solid ${colors.SUCCESS_200};
    .ant-alert-message {
      color: ${colors.WHITE};
      font-weight: 600;
    }
    .ant-alert-description {
      color: ${colors.WHITE};
    }
    .ant-alert-icon {
      color: ${colors.SUCCESS_200};
    }
  `,
}

// --- Helper functions (static, outside components) ---

const getUrlParams = () => {
  const searchParams = new URLSearchParams(window.location.search)
  return {
    origin: searchParams.get('origin'),
    data: searchParams.get('data'),
    code: searchParams.get('code'),
    status: searchParams.get('status')?.replace(/-/g, '_') || null,
    onboarding_sportspay: searchParams.get('onboarding_sportspay'),
    onboarding_stripe: searchParams.get('onboarding_stripe'),
  }
}

const getProviderErrorMessage = (providerError: ProviderError) => {
  if (!providerError) {
    return 'There Was An Error Connecting To The Payment Provider.'
  }
  return (
    providerError.message ||
    'There Was An Error Connecting To The Payment Provider.'
  )
}

const getProviderErrorDescription = (providerError: ProviderError) => {
  if (!providerError) {
    return 'Please Try Again Later Or Contact Support If The Issue Persists.'
  }
  if (providerError.code) {
    return `Error Code: ${providerError.code}. Please Try Again Later Or Contact Support If The Issue Persists.`
  }
  return 'Please Try Again Later Or Contact Support If The Issue Persists.'
}

// --- Sub-components ---

const AccountDetailsStatusRow = ({
  status = 'success',
  label,
}: {
  status: 'success' | 'error'
  label: string
}) => {
  return (
    <div
      css={css`
        display: flex;
        gap: 8px;
        justify-content: flex-start;
        align-items: center;
      `}
    >
      <Badge status={status} />
      <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
        {label}
      </Text>
    </div>
  )
}

const AccountInfoRow = ({ label, value }: { label: string; value: string }) => {
  if (value === '') {
    return null
  }

  return (
    <div
      css={css`
        display: flex;
        gap: 4px;
      `}
    >
      <Text variant='paragraphSmall' weight='bold' color={colors.NEUTRAL_0}>
        {label}:
      </Text>
      <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
        {value}
      </Text>
    </div>
  )
}

// --- Main component ---

const PaymentProviders = ({
  user,
  paymentProviderId,
  account,
}: {
  user: UserData
  paymentProviderId: string | null
  account: Account
}) => {
  const stripeOnboarding = useRef(false)
  const [updateAccountStatusError, setUpdateAccountStatusError] = useState<
    string | null
  >(null)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [showOtherProviders, setShowOtherProviders] = useState(false)
  // Read URL params once on mount — avoids new object reference each render
  // which would cause effects with urlParameters in deps to re-fire
  const [urlParameters] = useState(getUrlParams)
  const {
    loading,
    link,
    isOnboarded,
    accountDetails,
    error,
    errorDetails,
    fetchPaymentProvidersData,
    resetState,
    accountStatus,
    putAccountInfo,
    putAccountInfoForStripeOnboarding,
    fetchPaymentProviderUrl,
    providerMessage,
  } = usePaymentProviderConnection()

  // Derived state — React Compiler handles memoization
  const lAccountStatus = (() => {
    if (
      accountStatus === PaymentProviderStatus.INCOMPLETE &&
      urlParameters?.status === PaymentProviderStatus.IN_REVIEW
    ) {
      return PaymentProviderStatus.IN_REVIEW
    }
    return accountStatus || urlParameters?.status
  })()

  const shouldDisplayConnectButton =
    lAccountStatus !== PaymentProviderStatus.ACTIVE &&
    lAccountStatus !== PaymentProviderStatus.PENDING_VERIFICATION &&
    lAccountStatus !== PaymentProviderStatus.IN_REVIEW &&
    lAccountStatus !== PaymentProviderStatus.DISABLED &&
    lAccountStatus !== PaymentProviderStatus.SUSPENDED

  const countryId = account?.address?.country?.id
  const isSportsPaySupported =
    !!countryId && SUPPORTED_SPORTSPAY_COUNTRY_IDS.includes(countryId)

  const shouldDisplayOtherProviders =
    !(urlParameters?.code && urlParameters?.status) &&
    !paymentProviderId &&
    isSportsPaySupported

  // Effects
  useEffect(() => {
    if (user.account_id) {
      fetchPaymentProvidersData(user.account_id)
    }
    return () => {
      resetState()
    }
  }, [user.account_id, resetState, fetchPaymentProvidersData])

  useEffect(() => {
    const updateAccountStatus = async () => {
      try {
        if (!user.account_id) {
          return
        }
        const hasUrlParams = urlParameters?.code && urlParameters?.status
        const shouldUpdate =
          (hasUrlParams &&
            accountStatus === PaymentProviderStatus.INCOMPLETE &&
            urlParameters?.status === PaymentProviderStatus.IN_REVIEW) ||
          (hasUrlParams && !paymentProviderId)

        if (shouldUpdate) {
          await putAccountInfo(
            urlParameters.code as string,
            urlParameters.status as string,
            user.account_id,
            '2' as PaymentProviderId
          )
        }
      } catch (e) {
        const errorMessage = getErrorMessageFromError(e)
        setUpdateAccountStatusError(errorMessage)
      }
    }
    updateAccountStatus()
  }, [
    urlParameters,
    putAccountInfo,
    user.account_id,
    paymentProviderId,
    accountStatus,
  ])

  useEffect(() => {
    const updateAccountStatusForStripeOnboarding = async () => {
      try {
        if (!urlParameters?.origin?.includes('onboarding_stripe')) {
          return
        }
        if (stripeOnboarding.current) {
          return
        }
        if (!user?.account_id) {
          return
        }
        if (paymentProviderId) {
          return
        }
        if (account?.payment_provider_type?.id) {
          return
        }
        await putAccountInfoForStripeOnboarding(
          user?.account_id,
          '1' as PaymentProviderId
        )
        await fetchPaymentProvidersData(user?.account_id)
        stripeOnboarding.current = true
        // Clean the URL without reloading — state is already refreshed
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        )
      } catch (e) {
        const errorMessage = getErrorMessageFromError(e)
        setUpdateAccountStatusError(errorMessage)
      }
    }
    updateAccountStatusForStripeOnboarding()
  }, [
    urlParameters,
    putAccountInfoForStripeOnboarding,
    user?.account_id,
    fetchPaymentProvidersData,
    paymentProviderId,
    account?.payment_provider_type?.id,
  ])

  // Provider config objects
  const sportsPay: PaymentProviderProps = {
    id: 'sportspay',
    name: 'SportsPay',
    description:
      'An Easy, Integrated Payment Solution That Guarantees Users And Administrators Competitive Rates And A Simple Registration Experience.',
    logo: (
      <img
        src='/images/sportspay.png'
        alt='SportsPay'
        css={css`
          width: 32px;
          height: 32px;
          border-radius: 4px;
        `}
      />
    ),
    isRecommended: true,
    hasCompetitiveRates: true,
    connectUrl: link ? `${link}` : '',
    isConnected: isOnboarded,
    accountDetails: accountDetails,
    accountStatus: accountStatus,
    shouldDisplayConnectButton: shouldDisplayConnectButton,
  }

  const stripe: PaymentProviderProps = {
    id: 'stripe',
    name: 'Stripe',
    description:
      'Accept Payments Online And In Person And Build A More Profitable Business.',
    logo: (
      <img
        src='/images/stripe.png'
        alt='Stripe'
        css={css`
          width: 32px;
          height: 32px;
        `}
      />
    ),
    connectUrl: link || '',
    isConnected: isOnboarded,
    accountDetails: accountDetails,
    shouldDisplayConnectButton: shouldDisplayConnectButton,
  }

  const activeProvider =
    paymentProviderId === PAYMENT_PROVIDERS.STRIPE || !isSportsPaySupported
      ? stripe
      : sportsPay

  // Render helpers
  const renderStatusBanner = () => {
    if (!lAccountStatus) {
      return null
    }
    switch (lAccountStatus) {
      case PaymentProviderStatus.NOT_CONFIGURED:
        return (
          <Alert
            description='No Payment Provider Has Been Configured For This Account.'
            type='info'
            showIcon
            css={statusBannerCss.info}
          />
        )
      case PaymentProviderStatus.NOT_STARTED:
        return (
          <Alert
            description='Get Started By Connecting A Payment Provider Below.'
            type='info'
            showIcon
            css={statusBannerCss.info}
          />
        )
      case PaymentProviderStatus.STARTED:
      case PaymentProviderStatus.INCOMPLETE:
      case PaymentProviderStatus.ACTION_REQUIRED:
        return (
          <Alert
            description='You Still Need To Complete The Onboarding Process. Please Click On The Button Below To Continue.'
            type='warning'
            showIcon
            css={statusBannerCss.warning}
          />
        )
      case PaymentProviderStatus.PENDING_VERIFICATION:
      case PaymentProviderStatus.IN_REVIEW:
        return (
          <Alert
            description='Your Account Is Being Reviewed. You Will Receive An Email Once It Is Approved.'
            type='info'
            showIcon
            css={statusBannerCss.info}
          />
        )
      case PaymentProviderStatus.RESTRICTED:
        return (
          <Alert
            description='Your Account Has Restrictions That Require Immediate Action. Please Visit Your Provider Dashboard.'
            type='error'
            showIcon
            css={statusBannerCss.error}
          />
        )
      case PaymentProviderStatus.ACTIVE:
        return (
          <Alert
            description='Your Account Is Connected. You Can Now Start Accepting Payments.'
            type='success'
            showIcon
            css={statusBannerCss.success}
          />
        )
      case PaymentProviderStatus.DISABLED:
      case PaymentProviderStatus.SUSPENDED:
      case PaymentProviderStatus.ERROR:
        return (
          <Alert
            description='There Was An Issue With Your Payment Provider Account. Please Contact Support Or Visit Your Provider Dashboard.'
            type='error'
            showIcon
            css={statusBannerCss.error}
          />
        )
      default:
        return null
    }
  }

  return (
    <div
      css={css`
        margin-top: 16px;
      `}
    >
      <div
        css={css`
          background-color: ${colors.SECONDARY_300};
          padding: 16px;
          border-radius: 8px 8px 0 0;
          border: 1px solid ${colors.SECONDARY_200};
          margin: 0 auto;
        `}
      >
        <Text variant='heading4' weight='semiBold' color={colors.WHITE}>
          PAYMENT PROVIDER
        </Text>
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style='margin-top: 8px;'
        >
          Link Your Payment Processing Account To Manage Registration And
          Transactions.
        </Text>
      </div>

      <div
        css={css`
          margin: 0 auto;
          padding: 16px;
          background-color: ${colors.SECONDARY_300};
          border-radius: 0 0 8px 8px;
          border: 1px solid ${colors.SECONDARY_200};
          gap: 16px;
          display: flex;
          flex-direction: column;
        `}
      >
        {updateAccountStatusError && (
          <Alert
            description={updateAccountStatusError}
            type='error'
            showIcon
            css={statusBannerCss.error}
          />
        )}
        {error && (
          <Alert
            message={getProviderErrorMessage(
              errorDetails || ({} as ProviderError)
            )}
            description={getProviderErrorDescription(
              errorDetails || ({} as ProviderError)
            )}
            type='error'
            showIcon
            css={css`
              margin-bottom: 16px;
              background-color: rgba(255, 77, 79, 0.1);
              border: 1px solid ${colors.ERROR_100};

              .ant-alert-message {
                color: ${colors.WHITE};
                font-weight: 600;
              }

              .ant-alert-description {
                color: ${colors.NEUTRAL_50};
              }

              .ant-alert-icon {
                color: ${colors.ERROR_100};
              }
            `}
            action={
              <button
                css={css`
                  height: 32px;
                  display: flex;
                  padding: 8px 16px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  border-radius: 4px;
                  background-color: ${colors.ERROR_100};
                  border: none;
                  cursor: pointer;
                  color: white;
                  font-weight: 500;

                  &:hover {
                    background-color: ${colors.ERROR_75};
                  }
                `}
                onClick={() => fetchPaymentProvidersData(user.account_id)}
              >
                Retry
              </button>
            }
          />
        )}
        {loading ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              padding: 16px;
            `}
          >
            <Spin
              size='small'
              css={css`
                .ant-spin-text {
                  color: ${colors.WHITE};
                  margin-top: 8px;
                }
              `}
            />
          </div>
        ) : (
          <>
            {renderStatusBanner()}
            {connectError && (
              <Alert
                description={connectError}
                type='error'
                showIcon
                css={statusBannerCss.error}
              />
            )}
            {providerMessage &&
              !error &&
              !updateAccountStatusError &&
              !connectError &&
              !lAccountStatus && (
                <Alert
                  description={providerMessage}
                  type='info'
                  showIcon
                  css={statusBannerCss.info}
                />
              )}
            <PaymentProviderCard
              provider={activeProvider}
              account={account}
              fetchPaymentProviderUrl={fetchPaymentProviderUrl}
              onConnectError={setConnectError}
            />
          </>
        )}

        {shouldDisplayOtherProviders && (
          <div
            css={css`
              display: flex;
              align-items: center;
              cursor: pointer;
              user-select: none;
              gap: 8px;
            `}
            onClick={() => setShowOtherProviders(!showOtherProviders)}
          >
            <Icon
              name={showOtherProviders ? 'chevron-up' : 'chevron-down'}
              fontSize={16}
            />
            <Text variant='captionSmall' weight='semiBold' color={colors.WHITE}>
              OTHER PROVIDERS
            </Text>
          </div>
        )}

        {showOtherProviders &&
          isSportsPaySupported &&
          (loading ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                padding: 16px;
              `}
            >
              <Spin
                size='small'
                tip='Loading Payment Provider Information...'
                css={css`
                  .ant-spin-text {
                    color: ${colors.WHITE};
                    margin-top: 8px;
                  }
                `}
              />
            </div>
          ) : (
            <PaymentProviderCard
              provider={stripe}
              account={account}
              fetchPaymentProviderUrl={fetchPaymentProviderUrl}
              onConnectError={setConnectError}
            />
          ))}
      </div>
    </div>
  )
}

// --- PaymentProviderCard ---

const PaymentProviderCard = ({
  provider,
  account,
  fetchPaymentProviderUrl = () => {},
  onConnectError,
}: {
  provider: PaymentProviderProps
  account: Account
  fetchPaymentProviderUrl?: any
  onConnectError?: (error: string | null) => void
}) => {
  const [connecting, setConnecting] = useState(false)
  const isSportsPay = provider.id === 'sportspay'
  const isCanada = account?.address?.country?.id === COUNTRY_ID_CANADA

  console.log({
    provider,
    account,
  })

  if (provider.isConnected && provider.accountDetails) {
    const businessInfo = account

    console.log({
      businessInfo,
      account,
      provider,
    })

    return (
      <div
        css={css`
          padding: 16px;
          background-color: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_200};
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            {provider.logo}
            <Text variant='heading5' weight='regular' color={colors.WHITE}>
              {provider.name}
            </Text>
          </div>
          <a
            css={css`
              display: flex;
              gap: 6px;
              align-items: center;
            `}
            href={
              isSportsPay
                ? 'https://phq.sportspay.com'
                : 'https://dashboard.stripe.com/'
            }
            target='_blank'
            rel='noreferrer'
          >
            <p
              css={css`
                font-size: 14px;
                color: ${colors.DEFAULT_FLAIR};
              `}
            >
              Go to {provider.name} Dashboard
            </p>
            <Icon name='external-link-alt' color={colors.DEFAULT_FLAIR} />
          </a>
        </div>

        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <div
            css={css`
              padding: 16px;
              border-radius: 8px;
              border: 1px dashed ${colors.SOFT_STEEL};
              flex: 1;
            `}
          >
            <Text
              variant='captionXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              League Info
            </Text>
            <div
              css={css`
                display: flex;
                margin-top: 4px;
              `}
            >
              <div
                css={css`
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  margin-top: 4px;
                `}
              >
                <>
                  <Text
                    variant='heading5'
                    weight='semiBold'
                    color={colors.NEUTRAL_0}
                  >
                    {businessInfo?.company_name}
                  </Text>
                  <AccountInfoRow
                    label='Address'
                    value={
                      businessInfo?.address_street ||
                      businessInfo?.address?.street_1 ||
                      ''
                    }
                  />
                  <AccountInfoRow
                    label='City'
                    value={
                      businessInfo.address_city ||
                      businessInfo?.address?.city ||
                      ''
                    }
                  />
                  <AccountInfoRow
                    label='State/Province'
                    value={
                      businessInfo?.address_state_province ||
                      businessInfo?.address?.province?.name ||
                      ''
                    }
                  />
                  <AccountInfoRow
                    label='Country'
                    value={
                      businessInfo?.address_country ||
                      businessInfo?.address?.country?.name_full ||
                      ''
                    }
                  />
                  <AccountInfoRow
                    label='Postal Code'
                    value={
                      businessInfo?.address_postal_code ||
                      businessInfo?.address?.postal_code ||
                      ''
                    }
                  />
                  <AccountInfoRow
                    label='Phone'
                    value={businessInfo?.phone_number || ''}
                  />
                  <AccountInfoRow
                    label='Email'
                    value={businessInfo?.email || ''}
                  />
                  {businessInfo.organization?.url && !isSportsPay && (
                    <AccountInfoRow
                      label='Website'
                      value={businessInfo?.organization?.url || ''}
                    />
                  )}
                </>
              </div>
            </div>
          </div>

          <div
            css={css`
              padding: 16px;
              border-radius: 8px;
              border: 1px dashed ${colors.SOFT_STEEL};
              flex: 1;
              margin-top: 4px;
            `}
          >
            <Text
              variant='captionXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Account Details
            </Text>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-top: 12px;
              `}
            >
              <AccountInfoRow
                value={
                  isSportsPay
                    ? provider.accountDetails.merchant_id || ''
                    : provider.accountDetails.account?.id || ''
                }
                label='Account ID'
              />
              {!isSportsPay && (
                <AccountInfoRow
                  value={provider.accountDetails.account?.type || ''}
                  label='Account Type'
                />
              )}
              {isSportsPay ? (
                <AccountInfoRow
                  value={isCanada ? 'CAD' : 'USD'}
                  label='Default Currency'
                />
              ) : (
                <AccountInfoRow
                  value={
                    provider.accountDetails.account?.default_currency || ''
                  }
                  label='Default Currency'
                />
              )}
              {!isSportsPay && (
                <AccountInfoRow
                  value={provider.accountDetails.account?.email || ''}
                  label='Account Email'
                />
              )}
              <AccountDetailsStatusRow
                label='Details Submitted'
                status={
                  provider.accountDetails.can_accept_payments ||
                  provider.accountDetails.details_submitted ||
                  provider?.accountDetails?.account?.details_submitted
                    ? 'success'
                    : 'error'
                }
              />
              <AccountDetailsStatusRow
                label='Charges Enabled'
                status={
                  provider.accountDetails.can_accept_payments ||
                  provider.accountDetails.charges_enabled
                    ? 'success'
                    : 'error'
                }
              />
              <AccountDetailsStatusRow
                label='Payouts Enabled'
                status={
                  provider.accountDetails.can_accept_payments ||
                  provider.accountDetails.payouts_enabled
                    ? 'success'
                    : 'error'
                }
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      css={css`
        padding: 16px;
        background-color: ${colors.SECONDARY_300};
        border: 1px solid
          ${provider.isRecommended ? colors.PRIMARY : colors.SECONDARY_200};
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        `}
      >
        {provider.logo}
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          {provider.isRecommended && (
            <div
              css={css`
                padding: 8px;
                background-color: rgba(242, 180, 0, 0.1);
                border-radius: 4px;
              `}
            >
              <Text
                variant='captionXSmall'
                weight='semiBold'
                color={colors.PRIMARY}
              >
                RECOMMENDED
              </Text>
            </div>
          )}
          {provider.hasCompetitiveRates && (
            <div
              css={css`
                padding: 8px;
                background-color: rgba(90, 166, 255, 0.1);
                border-radius: 4px;
              `}
            >
              <Text
                variant='captionXSmall'
                weight='semiBold'
                color={colors.INFO_200}
              >
                MOST COMPETITIVE RATES
              </Text>
            </div>
          )}
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: flex-start;
          `}
        >
          <div>
            <Text variant='heading5' weight='regular' color={colors.WHITE}>
              {provider.name}
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.ATTENDANCE_GRAY}
              style='margin-top: 8px; max-width: 640px;'
            >
              {provider.description}
            </Text>
          </div>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          gap: 16px;
        `}
      >
        {provider.shouldDisplayConnectButton && (
          <button
            css={css`
              height: 40px;
              display: flex;
              padding: 8px 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 8px;
              background-color: ${colors.PRIMARY};
              cursor: ${connecting ? 'not-allowed' : 'pointer'};
              opacity: ${connecting ? 0.6 : 1};
            `}
            disabled={connecting}
            onClick={async () => {
              try {
                onConnectError?.(null)
                setConnecting(true)
                if (provider.connectUrl) {
                  const encodedUrl = encodeURI(provider.connectUrl)
                  window.open(encodedUrl, '_self')
                } else {
                  const response = await fetchPaymentProviderUrl(
                    account.uid,
                    provider?.id === 'sportspay' ? '2' : '1'
                  )
                  if (response?.url) {
                    window.open(response.url, '_self')
                  } else if (response?.message) {
                    onConnectError?.(response.message)
                  }
                }
              } catch (e: any) {
                const errorMessage = getErrorMessageFromError(e)
                onConnectError?.(errorMessage)
              } finally {
                setConnecting(false)
              }
            }}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={PRIMARY_BUTTON_CONTENT.text}
            >
              Connect to Your {provider.name} Account
            </Text>
            {connecting ? (
              <Spin size='small' />
            ) : (
              <ExportOutlined
                style={{ fontSize: 16, color: PRIMARY_BUTTON_CONTENT.icon }}
              />
            )}
          </button>
        )}
        {provider.contactUrl && (
          <button
            css={css`
              height: 40px;
              display: flex;
              padding: 8px 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 8px;
              background-color: transparent;
              border: 1px solid ${colors.WHITE};
              cursor: pointer;
            `}
            onClick={() => window.open(provider.contactUrl, '_blank')}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Contact Us
            </Text>
            <ExportOutlined style={{ fontSize: 16, color: colors.WHITE }} />
          </button>
        )}
      </div>
    </div>
  )
}

export default PaymentProviders
